import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import { addCourseLegend, courseMarkerIcon } from "../utils/courseMarkers"

const DEFAULT_CENTER = [53.38, -1.47]
const DEFAULT_ZOOM = 13

export default class extends Controller {
  static targets = [
    "map",
    "startLat",
    "startLng",
    "finishLat",
    "finishLng",
    "finishFields",
    "startModeBtn",
    "finishModeBtn",
    "hint",
  ]
  static values = {
    startEqualsFinish: Boolean,
  }

  connect() {
    this.placing = "start"
    this.startMarker = null
    this.finishMarker = null
    this.legendRef = { current: null }

    const start = this.readPair(this.startLatTarget, this.startLngTarget)
    const finish = this.readPair(this.finishLatTarget, this.finishLngTarget)
    const center = start || finish || DEFAULT_CENTER

    this.map = L.map(this.mapTarget).setView(center, DEFAULT_ZOOM)
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    }).addTo(this.map)

    if (start) this.setStart(start[0], start[1], false)
    if (!this.startEqualsFinishValue && finish) this.setFinish(finish[0], finish[1], false)
    this.updateLegend()

    this.map.on("click", (e) => {
      if (this.placing === "finish" && !this.startEqualsFinishValue) {
        this.setFinish(e.latlng.lat, e.latlng.lng, true)
      } else {
        this.setStart(e.latlng.lat, e.latlng.lng, true)
      }
    })

    this.updateModeUi()
    this.updateFinishVisibility()

    ;[this.startLatTarget, this.startLngTarget].forEach((el) => {
      el.addEventListener("change", () => {
        const pair = this.readPair(this.startLatTarget, this.startLngTarget)
        if (pair) this.setStart(pair[0], pair[1], false)
      })
    })

    if (this.hasFinishLatTarget) {
      ;[this.finishLatTarget, this.finishLngTarget].forEach((el) => {
        el.addEventListener("change", () => {
          const pair = this.readPair(this.finishLatTarget, this.finishLngTarget)
          if (pair) this.setFinish(pair[0], pair[1], false)
        })
      })
    }

    requestAnimationFrame(() => this.map.invalidateSize())
  }

  disconnect() {
    if (this.map) {
      if (this.legendRef?.current) {
        this.map.removeControl(this.legendRef.current)
        this.legendRef.current = null
      }
      this.map.remove()
      this.map = null
    }
  }

  setPlacingStart() {
    this.placing = "start"
    this.updateModeUi()
  }

  setPlacingFinish() {
    if (this.startEqualsFinishValue) return
    this.placing = "finish"
    this.updateModeUi()
  }

  updateModeUi() {
    if (this.hasHintTarget) {
      this.hintTarget.textContent = this.startEqualsFinishValue
        ? "Click the map to place Start/Finish. Drag the marker to adjust."
        : this.placing === "finish"
          ? "Click the map to place Finish. Drag markers to adjust."
          : "Click the map to place Start. Drag markers to adjust."
    }
    if (this.hasStartModeBtnTarget) {
      this.startModeBtnTarget.classList.toggle("active", this.placing === "start")
    }
    if (this.hasFinishModeBtnTarget) {
      this.finishModeBtnTarget.classList.toggle("active", this.placing === "finish")
    }
  }

  updateFinishVisibility() {
    if (!this.hasFinishFieldsTarget) return
    this.finishFieldsTarget.style.display = this.startEqualsFinishValue ? "none" : ""
  }

  readPair(latEl, lngEl) {
    const lat = parseFloat(latEl.value)
    const lng = parseFloat(lngEl.value)
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng]
    return null
  }

  writePair(latEl, lngEl, lat, lng) {
    latEl.value = Number(lat).toFixed(7)
    lngEl.value = Number(lng).toFixed(7)
  }

  setStart(lat, lng, writeFields) {
    if (writeFields) this.writePair(this.startLatTarget, this.startLngTarget, lat, lng)

    const label = this.startEqualsFinishValue ? "Start/Finish" : "Start"
    if (this.startMarker) {
      this.startMarker.setLatLng([lat, lng])
      this.startMarker.setIcon(courseMarkerIcon(label, "#16a34a"))
    } else {
      this.startMarker = L.marker([lat, lng], {
        draggable: true,
        icon: courseMarkerIcon(label, "#16a34a"),
      }).addTo(this.map)
      this.startMarker.on("dragend", () => {
        const pos = this.startMarker.getLatLng()
        this.writePair(this.startLatTarget, this.startLngTarget, pos.lat, pos.lng)
        if (this.startEqualsFinishValue) {
          this.writePair(this.finishLatTarget, this.finishLngTarget, pos.lat, pos.lng)
        }
      })
    }

    if (this.startEqualsFinishValue) {
      this.writePair(this.finishLatTarget, this.finishLngTarget, lat, lng)
    }
    this.updateLegend()
  }

  setFinish(lat, lng, writeFields) {
    if (this.startEqualsFinishValue) return
    if (writeFields) this.writePair(this.finishLatTarget, this.finishLngTarget, lat, lng)

    if (this.finishMarker) {
      this.finishMarker.setLatLng([lat, lng])
      this.finishMarker.setIcon(courseMarkerIcon("Finish", "#dc2626"))
    } else {
      this.finishMarker = L.marker([lat, lng], {
        draggable: true,
        icon: courseMarkerIcon("Finish", "#dc2626"),
      }).addTo(this.map)
      this.finishMarker.on("dragend", () => {
        const pos = this.finishMarker.getLatLng()
        this.writePair(this.finishLatTarget, this.finishLngTarget, pos.lat, pos.lng)
      })
    }
    this.updateLegend()
  }

  updateLegend() {
    if (!this.map) return

    const startPos = this.startMarker?.getLatLng()
    const finishPos = this.startEqualsFinishValue
      ? startPos
      : this.finishMarker?.getLatLng()

    addCourseLegend(
      this.map,
      {
        start: startPos ? { lat: startPos.lat, lng: startPos.lng } : null,
        finish: finishPos ? { lat: finishPos.lat, lng: finishPos.lng } : null,
      },
      this.legendRef,
      {
        items: this.startEqualsFinishValue
          ? [{ label: "Start/Finish", color: "#16a34a" }]
          : [
              { label: "Start", color: "#16a34a" },
              { label: "Finish", color: "#dc2626" },
            ],
      }
    )
  }
}
